import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Plus,
  Library,
  Scissors,
  Youtube,
  Settings,
  Activity,
  Cpu,
  LogOut,
} from "lucide-react";
import { TID } from "@/lib/testIds";
import { useAuth } from "@/contexts/AuthContext";
import PipelineHealthBanner from "@/components/PipelineHealthBanner";
import SidebarLogoAnomalyDot from "@/components/SidebarLogoAnomalyDot";

const NAV = [
  { to: "/admin", icon: LayoutDashboard, label: "Dashboard", tid: TID.navDashboard, end: true },
  { to: "/admin/wizard", icon: Plus, label: "New Match", tid: TID.navNewSequence },
  { to: "/admin/library", icon: Library, label: "Matches", tid: TID.navLibrary },
  { to: "/admin/sequences", icon: Scissors, label: "Sequences", tid: "nav-sequences" },
  { to: "/admin/tactics", icon: Youtube, label: "Tactics (YT)", tid: "nav-tactics" },
  { to: "/admin/settings", icon: Settings, label: "Settings", tid: TID.navSettings },
];

export default function Sidebar() {
  const loc = useLocation();
  const { user, logout } = useAuth();
  return (
    <aside
      data-testid={TID.sidebar}
      className="w-64 shrink-0 border-r border-border bg-background flex flex-col h-screen sticky top-0"
    >
      {/* Brand */}
      <div className="px-5 py-5 border-b border-border">
        <div className="flex items-center gap-2.5">
          <div className="relative w-9 h-9 rounded-sm bg-primary/15 border border-primary/30 flex items-center justify-center">
            <Cpu className="w-5 h-5 text-primary" strokeWidth={2.25} />
            <SidebarLogoAnomalyDot />
          </div>
          <div className="leading-tight">
            <div className="font-display font-black uppercase text-sm tracking-wider text-foreground">
              Frisbee/X
            </div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              Tactic · Cutter
            </div>
          </div>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 py-4 space-y-0.5">
        <div className="px-2 mb-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
          Workspace
        </div>
        {NAV.map(({ to, icon: Icon, label, tid, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            data-testid={tid}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-sm text-sm font-medium transition-colors duration-150
              ${
                isActive
                  ? "bg-accent text-primary"
                  : "text-muted-foreground hover:bg-secondary hover:text-foreground"
              }`
            }
          >
            <Icon className="w-4 h-4" strokeWidth={2} />
            <span>{label}</span>
            {to === "/wizard" && (
              <span className="ml-auto font-mono text-[10px] uppercase text-primary/60">
                ⌘N
              </span>
            )}
          </NavLink>
        ))}
      </nav>

      {/* Footer */}
      <div className="px-4 py-4 border-t border-border space-y-3">
        {/* Pipeline Health · permanent but discrete · Phase G */}
        <PipelineHealthBanner />
        {user && (
          <div className="flex items-center gap-2.5">
            {user.picture ? (
              <img
                src={user.picture}
                alt=""
                referrerPolicy="no-referrer"
                className="w-7 h-7 rounded-full border border-border shrink-0"
              />
            ) : (
              <div className="w-7 h-7 rounded-full bg-primary/15 border border-primary/30 flex items-center justify-center font-mono text-[10px] text-primary shrink-0">
                {user.name?.[0] || user.email?.[0] || "?"}
              </div>
            )}
            <div className="min-w-0 flex-1 leading-tight">
              <div className="text-xs text-foreground truncate">{user.name}</div>
              <div className="font-mono text-[10px] text-muted-foreground truncate">{user.email}</div>
            </div>
            <button
              data-testid="sidebar-logout"
              onClick={async () => { await logout(); window.location.href = "/"; }}
              title="Sign out"
              className="text-muted-foreground hover:text-destructive transition-colors"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        )}
        <div>
          <div className="flex items-center gap-2 mb-1.5">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-60" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" />
            </span>
            <span className="font-mono text-[10px] uppercase tracking-widest text-foreground">
              System ready
            </span>
          </div>
          <div className="flex items-center justify-between font-mono text-[10px] text-muted-foreground">
            <span className="flex items-center gap-1">
              <Activity className="w-3 h-3" />
              YOLOv8x · ByteTRACK
            </span>
            <span>CPU</span>
          </div>
        </div>
      </div>
    </aside>
  );
}
